import { cityByAirport } from '@voll-intelligence/domain';

/** Separa "CÓDIGO - CIDADE" (ex.: "GRU - SAO PAULO") em código + cidade. Sem o
 *  separador, o valor todo é o código e a cidade é null. */
export function splitAirport(v: string | null | undefined): { code: string; city: string | null } {
  const s = v?.trim();
  if (!s) return { code: '', city: null };
  const i = s.indexOf(' - ');
  if (i === -1) return { code: s, city: null };
  return { code: s.slice(0, i).trim(), city: s.slice(i + 3).trim() || null };
}

/** Rótulo de cidade para LER: o nome curado do domain primeiro — ele já vem
 *  acentuado e em caixa de título ("GIG - RIO DE JANEIRO" → "Rio de Janeiro"), que é
 *  como o protótipo escreve a cidade numa linha de corpo. A fonte manda o aeroporto
 *  em caixa alta sem acento, e gritar o destino no meio de uma frase é ruído.
 *  Sem código conhecido, cai na parte-cidade crua e, sem ela, no valor todo. */
export function cityLabel(v: string | null | undefined): string {
  const { code, city } = splitAirport(v);
  return cityByAirport(v) ?? city ?? code;
}

/** "GRU São Paulo" — a sigla que se lê no painel do aeroporto e a cidade que a
 *  pessoa reconhece. Sem cidade conhecida sobra a sigla; sem sigla, o valor cru. */
export function placeLabel(airport: string | null | undefined): string {
  const { code } = splitAirport(airport);
  const city = cityByAirport(airport);
  if (!code) return city ?? '—';
  return city && city !== code ? `${code} ${city}` : code;
}

/** Duração legível "Xh YY" / "YYmin". O provider manda formatos diferentes:
 *  segundos puros ("4200" = durationTimeInSeconds), ISO-8601 ("PT9H55M") ou já
 *  formatado ("1h45", "9h55") — este último volta cru. Null quando ausente. */
export function formatFlightDuration(raw: string | null | undefined): string | null {
  const v = raw?.trim();
  if (!v) return null;
  const fromMinutes = (mins: number): string => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${String(m)}min`;
    return m === 0 ? `${String(h)}h` : `${String(h)}h${String(m).padStart(2, '0')}`;
  };
  if (/^\d+$/.test(v)) return fromMinutes(Math.round(Number(v) / 60));
  const iso = /^PT(?:(\d+)H)?(?:(\d+)M)?$/i.exec(v);
  if (iso && (iso[1] || iso[2])) return fromMinutes(Number(iso[1] ?? 0) * 60 + Number(iso[2] ?? 0));
  return v;
}

/* Os rótulos de real que a fonte manda no lugar do código ("Real - R$", #5136). */
const BRL_LABELS = new Set(['REAL - R$', 'R$', 'REAL', 'REAIS']);

/** O código ISO-4217 do que a fonte manda em `currency`, ou `null` quando não há forma
 *  de código nem rótulo conhecido — nunca uma moeda inventada. */
export function currencyCode(raw: string): string | null {
  if (/^[A-Za-z]{3}$/.test(raw)) return raw.toUpperCase();
  return BRL_LABELS.has(raw.toUpperCase()) ? 'BRL' : null;
}

/** `null` quando falta valor OU moeda — nunca um "R$ —" ou um zero mentiroso;
 *  o chamador decide o que aparece no lugar de um preço ausente.
 *
 *  Nunca lança: a moeda é dado de fornecedor, e o `Intl` lança `RangeError` para
 *  o que não tem forma de código ISO-4217 — numa linha de lista, isso derruba a
 *  tela inteira. O rótulo que não vira código aparece cru antes do valor. */
export function money(amount: number | null, currency: string | null): string | null {
  const raw = currency?.trim();
  if (amount == null || !raw) return null;
  const code = currencyCode(raw);
  // Moeda NÃO segue o locale da UI: formato brasileiro fixo, moeda do backend.
  // (datas seguem o idioma — ver os formatadores de `dates.ts`.)
  if (code) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: code }).format(amount);
  }
  const value = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${raw} ${value}`;
}

// O IATA da companhia pelo nome mora no domain (#7098): o e-mail da viagem também o usa.
export { airlineIata } from '@voll-intelligence/domain';
